import axios from 'axios';
import { DeviceInfo, SessionParams, TimeStatus, User } from '../structure/local';
import {
  NvrResponse,
  RemoteDeviceInfo, RemoteSessionParams,
  RemoteTimeStatus, RemoteUserList
} from '../structure/remote';
import { LTR, RTL } from '../structure/transform';
import { formatDate } from '../utils/Common';
import { sha256 } from '../utils/Sha256';
import { XmlHandler } from '../utils/XmlHandler';
import { ErrorHandler } from '../utils/ErrorHandler';
import { ApiError } from '../errors/HikvisionError';
import { Logger } from '../interfaces/Logger';
import { HttpClient } from '../interfaces/HttpClient';
import { ConfigValidator, ConfigSchema } from '../interfaces/ConfigValidator';

export interface BaseConfig {
  ip: string
  port?: number
  user: string
  password: string
  timeout?: number
  proxy?: string
  version?: number
  userPassword?: string
}

export abstract class Base {
  protected readonly ip: string;
  protected readonly port: number;
  protected readonly user: string;
  protected readonly password: string;
  protected session: string = '';
  protected readonly useProxy: boolean;
  protected readonly wsPort: number;
  public readonly version: number;
  protected readonly userPassword: string;

  protected get url(): string {
    return `http://${this.ip}:${this.port}`;
  }

  protected get headers(): Record<string, string> {
    return this.useProxy
      ? {
        proxy: this.url,
        Cookie: this.version === 2 ? this.session : `WebSession=${this.session}`
      }
      : {
        Cookie: this.version === 2 ? this.session : `WebSession=${this.session}`
      };
  }

  protected constructor(
    config: BaseConfig,
    protected readonly httpClient: HttpClient,
    protected readonly logger: Logger,
    protected readonly configValidator: ConfigValidator
  ) {
    // Validate configuration
    const validatedConfig = this.configValidator.validate(config, this.getConfigSchema());
    
    this.ip = validatedConfig.ip;
    this.port = validatedConfig.port ?? 80;
    this.user = validatedConfig.user;
    this.password = validatedConfig.password;
    this.useProxy = !!validatedConfig.proxy;
    this.version = validatedConfig.version ?? 1;
    this.userPassword = validatedConfig.userPassword ?? '';
    this.wsPort = 7681;

    this.logger.info('Base class initialized', {
      ip: this.ip,
      port: this.port,
      user: this.user,
      version: this.version,
      useProxy: this.useProxy
    });
  }

  private getConfigSchema(): ConfigSchema<BaseConfig> {
    return {
      required: ['ip', 'user', 'password'],
      optional: ['port', 'timeout', 'proxy', 'version', 'userPassword'],
      validators: {
        ip: (value: string) => /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(value),
        port: (value: number) => value > 0 && value <= 65535,
        user: (value: string) => value.length > 0,
        password: (value: string) => value.length > 0
      }
    };
  }

  public getSession(): string {
    return this.session;
  }

  protected async getSessionParams(): Promise<SessionParams> {
    const response = await this.httpClient.get(
      `/ISAPI/Security/sessionLogin/capabilities?username=${this.user}`, {
      headers: this.headers
    });
    const json = await XmlHandler.parser<RemoteSessionParams>(response.data);
    return RTL.sessionParams(json);
  }

  public async deviceInfo(): Promise<DeviceInfo> {
    const response = await this.httpClient.get('/ISAPI/System/deviceInfo', {
      headers: this.headers
    });
    const info = await XmlHandler.parser<RemoteDeviceInfo>(response.data);
    return RTL.deviceInfo(info);
  }

  public async updateDeviceInfo(deviceInfo: DeviceInfo): Promise<void> {
    const response = await this.httpClient.put('ISAPI/System/deviceInfo',
      XmlHandler.build(LTR.deviceInfo(deviceInfo)),
      {
        headers: this.headers
      });
    await XmlHandler.parser<void>(response.data);
  }

  public async getTime(): Promise<TimeStatus> {
    const response = await this.httpClient.get('ISAPI/System/time', {
      headers: this.headers
    });
    const json = await XmlHandler.parser<RemoteTimeStatus>(response.data);
    return RTL.timeStatus(json);
  }

  public async setTime(timeStatus: TimeStatus): Promise<void> {
    await this.httpClient.put('/ISAPI/System/time',
      XmlHandler.build({
        Time: {
          timeMode: 'manual',
          localTime: formatDate(timeStatus.time, 'yyyy-MM-ddThh:mm:ss'),
          timeZone: 'CST-8:00:00'
        }
      }),
      {
        headers: this.headers
      });
  }

  public async addUser(user: User): Promise<void> {
    return ErrorHandler.withErrorHandling(async () => {
      let result: string;
      try {
        const response = await this.httpClient.post('/ISAPI/Security/users',
          XmlHandler.build({
            User: {
              id: user.id,
              userName: user.userName,
              loginPassword: this.password,
              password: user.password ?? this.userPassword,
              userLevel: 'Operator'
            }
          }),
          {
            headers: this.headers
          });
        result = response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          result = error.response.data;
        } else {
          throw error;
        }
      }
      const r = await XmlHandler.parser<NvrResponse>(result);
      if (r.ResponseStatus.statusCode !== '1') {
        throw new ApiError(
          r.ResponseStatus.subStatusCode || 'Failed to add user',
          undefined,
          r.ResponseStatus.subStatusCode
        );
      }
    }, 'addUser');
  }

  public async updateUser(user: User): Promise<void> {
    return ErrorHandler.withErrorHandling(async () => {
      let result: string;
      try {
        const response = await this.httpClient.put(`/ISAPI/Security/users/${user.id}`,
          XmlHandler.build({
            User: {
              id: user.id,
              userName: user.userName,
              loginPassword: this.password,
              password: user.password ?? this.userPassword,
              userLevel: user.type
            }
          }),
          {
            headers: this.headers
          });
        result = response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          result = error.response.data;
        } else {
          throw error;
        }
      }
      const r = await XmlHandler.parser<NvrResponse>(result);
      if (r.ResponseStatus.statusCode !== '1') {
        throw new ApiError(
          r.ResponseStatus.subStatusCode || 'Failed to update user',
          undefined,
          r.ResponseStatus.subStatusCode
        );
      }
    }, 'updateUser');
  }

  public async deleteUser(data: number | string): Promise<void> {
    return ErrorHandler.withErrorHandling(async () => {
      let userId: number = typeof data === 'number' ? data : 0;

      if (typeof data === 'string') {
        const users = await this.fetchUsers();
        const user = users.find(u => u.userName === data);
        if (!user) {
          throw new ApiError(`User with name '${data}' not found`);
        }
        userId = user.id;
      }

      let result: string;
      try {
        const response = await this.httpClient.delete(`/ISAPI/Security/users/${userId}`, {
          headers: this.headers,
          params: {
            loginPassword: this.password
          }
        });
        result = response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          result = error.response.data;
        } else {
          throw error;
        }
      }

      const r = await XmlHandler.parser<NvrResponse>(result);
      if (r.ResponseStatus.statusCode !== '1') {
        throw new ApiError(
          r.ResponseStatus.subStatusCode || 'Failed to delete user',
          undefined,
          r.ResponseStatus.subStatusCode
        );
      }
    }, 'deleteUser');
  }

  public async fetchUsers(): Promise<User[]> {
    const response = await this.httpClient.get('/ISAPI/Security/users', {
      headers: this.headers
    });
    const users = await XmlHandler.parser<RemoteUserList>(response.data);
    return RTL.fetchUsers(users);
  }

  public async getUsers(): Promise<User[]> {
    const response = await this.httpClient.get('/ISAPI/Security/users', {
      headers: this.headers
    });
    const users = await XmlHandler.parser<RemoteUserList>(response.data);
    return RTL.fetchUsers(users);
  }

  protected encodePassword(session: SessionParams): string {
    let result: string;
    if (session.isIrreversible) {
      result = sha256(this.user + session.salt + this.password);
      result = sha256(result + session.challenge);
      for (let f = 2; f < session.iterations; f++) {
        result = sha256(result);
      }
    } else {
      result = sha256(this.password) + session.challenge;
      for (let f = 1; f < session.iterations; f++) {
        result = sha256(result);
      }
    }
    return result;
  }

  protected async reboot(): Promise<void> {
    return ErrorHandler.withErrorHandling(async () => {
      let result: string;
      try {
        const response = await this.httpClient.put('/ISAPI/System/reboot', null, {
          headers: this.headers
        });
        result = response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          result = error.response.data;
        } else {
          throw error;
        }
      }
      const r = await XmlHandler.parser<NvrResponse>(result);
      if (r.ResponseStatus.statusCode !== '1') {
        throw new ApiError(
          r.ResponseStatus.subStatusCode || 'Failed to reboot device',
          undefined,
          r.ResponseStatus.subStatusCode
        );
      }
    }, 'reboot');
  }
}
